import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import { projectAPI } from "../../services/api";
import "leaflet/dist/leaflet.css";

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const ProjectMap = ({
  projects, // projects จาก API ที่ filter แล้ว (9 รายการ)
  loading,
  currentProvince,
  onProvinceChange,
}) => {
  const [allProjects, setAllProjects] = useState([]); // โครงการทั้งหมด
  const [displayProjects, setDisplayProjects] = useState([]); // โครงการที่จะแสดงบนแผนที่
  const [loadingAll, setLoadingAll] = useState(false);

  // ดึงข้อมูลโครงการทั้งหมด (เมื่อไม่มี filter)
  useEffect(() => {
    const fetchAllProjects = async () => {
      setLoadingAll(true);
      try {
        // ดึงข้อมูลโครงการทั้งหมดที่มีพิกัด
        const response = await projectAPI.getLocations();
        console.log("All projects from API:", response.data);

        if (response.data && response.data.success) {
          setAllProjects(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch all projects:", error);
      } finally {
        setLoadingAll(false);
      }
    };

    fetchAllProjects();
  }, []);

  // ตัดสินใจว่าจะใช้ข้อมูลชุดไหนแสดงบนแผนที่
  useEffect(() => {
    // ตรวจสอบว่ามี filter หรือไม่
    const hasFilters = currentProvince && currentProvince !== "";

    if (hasFilters) {
      // ถ้ามี filter: ใช้ projects ที่ส่งมาจาก ProjectsPage (ซึ่ง filter แล้ว)
      console.log("Using filtered projects:", projects.length);

      // กรองเฉพาะที่มีพิกัด
      const withLocation = projects.filter(
        (project) =>
          project.lat &&
          project.lng &&
          project.lat !== "" &&
          project.lng !== "",
      );
      setDisplayProjects(withLocation);
    } else {
      // ถ้าไม่มี filter: ใช้ allProjects ที่ดึงมาทั้งหมด
      console.log("Using all projects:", allProjects.length);
      setDisplayProjects(allProjects);
    }
  }, [projects, allProjects, currentProvince]);

  const createCustomIcon = (color) => {
    return L.divIcon({
      className: "custom-marker",
      html: `<i class="fas fa-map-marker-alt" style="color: ${color}; font-size: 30px; text-shadow: 2px 2px 4px rgba(0,0,0,0.3);"></i>`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30],
    });
  };

  if (loading || loadingAll) {
    return (
      <div className="h-[600px] flex items-center justify-center bg-gray-100">
        <div className="spinner"></div>
      </div>
    );
  }

  const center =
    displayProjects.length > 0
      ? [parseFloat(displayProjects[0].lat), parseFloat(displayProjects[0].lng)]
      : [15.0903, 104.3336]; // ศรีสะเกษ

  return (
    <div className="relative">
      {/* Province Filter Info */}
      {currentProvince && (
        <div className="absolute top-4 left-4 z-10 bg-white px-4 py-2 rounded-lg shadow-lg">
          <span className="text-sm font-medium text-gray-700">
            <i className="fas fa-filter mr-2 text-primary-500"></i>
            จังหวัด: {currentProvince}
          </span>
          <button
            onClick={() => onProvinceChange("")}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {/* Project Count */}
      <div className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-gray-100">
        <span className="text-sm font-sans font-medium text-gray-700">
          <i className="fas fa-map-marker-alt mr-2 text-primary-500"></i>
          พบ {displayProjects.length} โครงการ
        </span>
      </div>

      <MapContainer
        center={center}
        zoom={9}
        style={{ height: "600px", width: "100%" }}
        className="rounded-lg shadow-inner font-sans"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />

        {displayProjects.map((project) => (
          <Marker
            key={project.project_id}
            position={[parseFloat(project.lat), parseFloat(project.lng)]}
            icon={createCustomIcon(project.dpt_color)}
          >
            <Popup className="font-sans">
              <div className="w-64 bg-white rounded-lg overflow-hidden font-sans">
                {/* Header with color border */}
                <div
                  className="h-2 w-full"
                  style={{ backgroundColor: project.dpt_color }}
                ></div>

                <div className="p-4">
                  {/* Title */}
                  <h3 className="text-base font-semibold text-gray-800 mb-3 line-clamp-2">
                    {project.project_name}
                  </h3>

                  {/* Department */}
                  <p
                    className="text-sm font-medium mb-2 flex items-center gap-1"
                    style={{ color: project.dpt_color }}
                  >
                    <i className="fas fa-building text-xs"></i>
                    {project.dpt_name}
                  </p>

                  {/* Project Leader */}
                  <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                    <i className="fas fa-user text-primary-500 text-xs"></i>
                    {project.mem_fname} {project.mem_sname}
                  </p>

                  {/* Location */}
                  <p className="text-sm text-gray-600 mb-4 flex items-center gap-1">
                    <i className="fas fa-map-marker-alt text-primary-500 text-xs"></i>
                    {project.subdistrict} {project.district} {project.province}
                  </p>

                  {/* View Detail Button */}
                  <Link
                    to={`/projects/${project.project_id}`}
                    className="inline-flex items-center justify-center w-full gap-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                  >
                    ดูรายละเอียด
                    <i className="fas fa-arrow-right text-xs"></i>
                  </Link>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {displayProjects.length === 0 && !loading && !loadingAll && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90">
          <div className="text-center">
            <i className="fas fa-map-marked-alt text-5xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              ไม่พบโครงการในพื้นที่
            </h3>
            <p className="text-gray-500 mb-4">
              {currentProvince
                ? `ไม่มีโครงการในจังหวัด ${currentProvince}`
                : "ไม่มีโครงการที่มีข้อมูลพิกัด"}
            </p>
            {currentProvince && (
              <button
                onClick={() => onProvinceChange("")}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                ดูทั้งหมด
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectMap;
